<script>
export default {
  name: "VvCouponCard",
  props: {
    item: {
      type: Object,
      required: true
    },
    dataTest: {
      type: String,
      required: true
    },
    fluid: {
      type: Boolean,
      default: false
    },
    interactive: {
      type: Boolean,
      default: true
    },
    showAction: {
      type: Boolean,
      default: true
    },
    history: {
      type: Boolean,
      default: false
    },
    subtitle: {
      type: String,
      default: ""
    }
  },
  emits: ["select", "use"],
  computed: {
    title() {
      return this.item.coupon_language?.[0]?.name || this.item.name || "-";
    },
    image() {
      return (
        this.item.image_url || require("@/assets/images/default-image.png")
      );
    },
    expiredDate() {
      if (!this.item.expired_date) return null;
      return this.$moment(this.item.expired_date).format("DD MMM YY");
    },
    expiredTime() {
      if (!this.item.expired_date) return null;
      return this.$moment(this.item.expired_date).format("(HH:mm)");
    },
    historyStatusLabel() {
      return Number(this.item.already_use) === 1
        ? this.$t("already_used")
        : this.$t("privilege_expiration_label");
    }
  },
  methods: {
    onUse() {
      this.$emit("use", this.item);
    }
  }
};
</script>

<template>
  <article
    :class="[
      'vv-coupon-card',
      {
        'vv-coupon-card--fluid': fluid,
        'vv-coupon-card--static': !interactive
      }
    ]"
    :data-test="dataTest"
    @click="interactive && $emit('select', item)"
  >
    <div class="vv-coupon-card__image-wrap">
      <img
        :src="image"
        :alt="title"
        class="vv-coupon-card__image"
        :data-test="`${dataTest}-img`"
        loading="lazy"
      />
    </div>

    <div class="vv-coupon-card__content">
      <span class="vv-coupon-card__perforation" aria-hidden="true"></span>

      <h3 class="vv-coupon-card__title">{{ title }}</h3>

      <div v-if="expiredDate" class="vv-coupon-card__meta">
        <span>{{ $t("redeem_date") }} {{ expiredDate }}</span>
        <span v-if="expiredTime">{{ expiredTime }}</span>
      </div>
      <div v-else-if="subtitle" class="vv-coupon-card__meta">
        {{ subtitle }}
      </div>

      <div
        v-if="history"
        class="vv-coupon-card__history-status"
        :data-test="`${dataTest}-status-msg`"
      >
        {{ historyStatusLabel }}
      </div>

      <button
        v-else-if="showAction"
        type="button"
        class="vv-coupon-card__use-btn"
        :data-test="`${dataTest}-use-btn`"
        @click.stop="onUse"
      >
        <font-awesome-icon icon="gift" class="vv-coupon-card__use-icon" />
        {{ $t("use_coupon") || "ใช้คูปอง" }}
      </button>
    </div>
  </article>
</template>

<style lang="scss" scoped>
/*
  Vision Venture — Coupon Card (ticket-shape)
  - Figma frame 1:10721 "Coupon CardV2"
  - Layout: image ซ้าย 124x124 + content ขวา 214x124
  - Ticket notch: anchored to the actual media/content seam
  - Geometry can be overridden by --vv-coupon-* custom properties
  - Content bg: API-derived secondary surface
  - Button: API button color, radius 6
*/

.vv-coupon-card {
  --vv-coupon-card-width: 338px;
  --vv-coupon-media-width: 124px;
  --vv-coupon-min-height: 124px;
  --vv-coupon-notch-size: 20px;
  --vv-coupon-radius: 16px;

  position: relative;
  display: flex;
  align-items: stretch;
  width: var(--vv-coupon-card-width);
  flex: 0 0 var(--vv-coupon-card-width);
  cursor: pointer;
  filter: drop-shadow(0 4px 4px var(--primary-color-low));
  container-type: inline-size;
}

.vv-coupon-card--fluid {
  width: 100%;
  flex: 1 1 100%;
}

.vv-coupon-card--static {
  cursor: default;
}

.vv-coupon-card__image-wrap {
  position: relative;
  flex: 0 0 var(--vv-coupon-media-width);
  width: var(--vv-coupon-media-width);
  min-height: var(--vv-coupon-min-height);
  border-radius: var(--vv-coupon-radius) 0 0 var(--vv-coupon-radius);
  overflow: hidden;
  background: #fff;
  -webkit-mask:
    radial-gradient(
        circle at 100% 0,
        transparent calc(var(--vv-coupon-notch-size) / 2 - 0.5px),
        #000 calc(var(--vv-coupon-notch-size) / 2 + 0.5px)
      )
      top right / 100% 51% no-repeat,
    radial-gradient(
        circle at 100% 100%,
        transparent calc(var(--vv-coupon-notch-size) / 2 - 0.5px),
        #000 calc(var(--vv-coupon-notch-size) / 2 + 0.5px)
      )
      bottom right / 100% 51% no-repeat;
  mask:
    radial-gradient(
        circle at 100% 0,
        transparent calc(var(--vv-coupon-notch-size) / 2 - 0.5px),
        #000 calc(var(--vv-coupon-notch-size) / 2 + 0.5px)
      )
      top right / 100% 51% no-repeat,
    radial-gradient(
        circle at 100% 100%,
        transparent calc(var(--vv-coupon-notch-size) / 2 - 0.5px),
        #000 calc(var(--vv-coupon-notch-size) / 2 + 0.5px)
      )
      bottom right / 100% 51% no-repeat;
}

.vv-coupon-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.vv-coupon-card__content {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px 12px;
  border: var(--crm-card-glass-border);
  border-left: 0;
  background: var(--crm-card-glass-background);
  -webkit-backdrop-filter: var(--crm-card-glass-backdrop-filter);
  backdrop-filter: var(--crm-card-glass-backdrop-filter);
  border-radius: 0 var(--vv-coupon-radius) var(--vv-coupon-radius) 0;
  -webkit-mask:
    radial-gradient(
        circle at 0 0,
        transparent calc(var(--vv-coupon-notch-size) / 2 - 0.5px),
        #000 calc(var(--vv-coupon-notch-size) / 2 + 0.5px)
      )
      top left / 100% 51% no-repeat,
    radial-gradient(
        circle at 0 100%,
        transparent calc(var(--vv-coupon-notch-size) / 2 - 0.5px),
        #000 calc(var(--vv-coupon-notch-size) / 2 + 0.5px)
      )
      bottom left / 100% 51% no-repeat;
  mask:
    radial-gradient(
        circle at 0 0,
        transparent calc(var(--vv-coupon-notch-size) / 2 - 0.5px),
        #000 calc(var(--vv-coupon-notch-size) / 2 + 0.5px)
      )
      top left / 100% 51% no-repeat,
    radial-gradient(
        circle at 0 100%,
        transparent calc(var(--vv-coupon-notch-size) / 2 - 0.5px),
        #000 calc(var(--vv-coupon-notch-size) / 2 + 0.5px)
      )
      bottom left / 100% 51% no-repeat;
}

/* Vertical dashed separator between notches (ticket perforation) */
.vv-coupon-card__perforation {
  position: absolute;
  top: calc(var(--vv-coupon-notch-size) / 2 + 4px);
  bottom: calc(var(--vv-coupon-notch-size) / 2 + 4px);
  left: 0;
  width: 1px;
  background-image: linear-gradient(
    to bottom,
    var(--tab-text-color) 0,
    var(--tab-text-color) 4px,
    transparent 4px,
    transparent 8px
  );
  background-size: 1px 8px;
  background-repeat: repeat-y;
  pointer-events: none;
}

/* Compact containers scale the ticket geometry without moving the seam. */
@container (max-width: 319px) {
  .vv-coupon-card__image-wrap,
  .vv-coupon-card__content {
    --vv-coupon-notch-size: 16px;
  }

  .vv-coupon-card__image-wrap {
    flex-basis: 108px;
    width: 108px;
    min-height: 108px;
    border-radius: 14px 0 0 14px;
  }

  .vv-coupon-card__content {
    padding: 9px 10px;
    border-radius: 0 14px 14px 0;
  }
}

@container (max-width: 259px) {
  .vv-coupon-card__image-wrap,
  .vv-coupon-card__content {
    --vv-coupon-notch-size: 14px;
  }

  .vv-coupon-card__image-wrap {
    flex-basis: 92px;
    width: 92px;
    min-height: 104px;
    border-radius: 12px 0 0 12px;
  }

  .vv-coupon-card__content {
    padding: 8px;
    border-radius: 0 12px 12px 0;
  }
}

.vv-coupon-card__title {
  margin: 0;
  color: var(--text-color);
  font-size: var(--font-body-lg);
  font-weight: 700;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.vv-coupon-card__meta {
  display: flex;
  align-items: center;
  gap: 3px;
  color: var(--tab-text-color);
  font-size: var(--font-body-sm);
  line-height: 1.4;
}

.vv-coupon-card__use-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  padding: 7px 8px;
  border: 0;
  border-radius: 6px;
  background: var(--button-color);
  color: var(--font-button-color);
  font-size: var(--font-body);
  font-weight: 600;
  line-height: 1.4;
  cursor: pointer;
}

.vv-coupon-card__use-icon {
  font-size: var(--font-body);
}

.vv-coupon-card__history-status {
  width: 100%;
  padding: 7px 8px;
  border-radius: 2px;
  background: var(--primary-color-low);
  color: var(--tab-text-color);
  font-size: var(--font-body-sm);
  line-height: 1.4;
  text-align: center;
}
</style>
